import React, {useEffect, useRef, useState} from "react";
import clsx from "clsx";
import {Button, Label, Radio} from "flowbite-react";
import {RenderableTitle} from "data/models/survey/base";
import {QUESTION_CONTAINER_CLASS} from "components/Survey/classes";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown} from "@fortawesome/free-solid-svg-icons";
import {BrandTraitKey, traitName} from "data/models/shared/bc-sheet/brand-trait";
import LoadingSurvey from "components/Survey/LoadingSurvey";
import {assertNoError} from "helpers/asserts";
import {ColorDefinition} from "data/models/shared/recommended-colors3";
import {useRecommendedColors3} from "data/access/survey-question-pages/colors3";
import {calculateBackgroundColor} from "helpers/color-calculator";
import {SelectedColor, SelectedColors} from "data/models/survey/colors";

export type Colors3Select_SurveyQuestionPage_Props = {
  visible: boolean;
  title: RenderableTitle;
  questionId: string;
  value: SelectedColors | undefined;
  onChange: (id: string, value: SelectedColors) => void;
}

export default function Colors3SelectSurveyQuestionPage(
    {
      visible,
      title,
      questionId,
      value,
      onChange
    }: Colors3Select_SurveyQuestionPage_Props
) {
  const [recommendedColors, recommendedColorsLoading, recommendedColorsError] = useRecommendedColors3();
  const [openedColorsGroup, setOpenedColorsGroup] = useState(1);
  const showMoreRef = useRef<null | HTMLButtonElement>(null)

  useEffect(() => {
    // Scroll into "Show More" button when "openedFontsGroup" changes.
    if (openedColorsGroup > 1 && showMoreRef !== null && showMoreRef.current !== null) {
      showMoreRef.current.scrollIntoView({behavior: "smooth"})
    }
  }, [openedColorsGroup, showMoreRef]);

  assertNoError(recommendedColorsError);
  if (recommendedColorsLoading) {
    return visible && <LoadingSurvey /> || null;
  }

  function handleShowMore() {
    if (openedColorsGroup < (recommendedColors?.length ?? 0)) {
      setOpenedColorsGroup(openedColorsGroup + 1);
    }
  }

  function getSelectedColors(colorDefinition: ColorDefinition, trait: BrandTraitKey): SelectedColors {
    const getSelectedColor = (color: string): SelectedColor => ({
      trait,
      color,
    });

    return {
      primary: getSelectedColor(colorDefinition.primary),
      background: getSelectedColor(calculateBackgroundColor(colorDefinition.primary)),
      secondary: getSelectedColor(colorDefinition.secondary),
      accent: getSelectedColor(colorDefinition.accent),
    }
  }

  function renderColorDefinitionKey(colorDefinition: ColorDefinition): string {
    return `${colorDefinition.primary}-${colorDefinition.secondary}-${colorDefinition.accent}`;
  }

  function renderSelectedColorsKey(selectedColors?: SelectedColors): string | undefined {
    if (selectedColors === undefined) {
      return undefined;
    }
    return `${selectedColors.primary.color}-${selectedColors.secondary.color}-${selectedColors.accent.color}`;
  }

  return <div className={"flex flex-col w-full"}>
    <div className={clsx(QUESTION_CONTAINER_CLASS, "mb-24")}>
      <div className={"my-4 text-center"}>
        <span className={"text-3xl font-bold"}>{title}</span>
      </div>
      <div className={"text-center"}>
        <p className={"my-8 font-normal text-xl text-neutral-500"}>
          Based on your brand traits, we recommend the following color palettes.
        </p>
      </div>
      <div className={"mb-8 text-center"}>
        <p>
          <span className={"text-black text-xl font-bold"}>Select one group.</span>
        </p>
      </div>
      <div>
        <fieldset name={questionId} className="flex flex-col gap-4 justify-center" autoFocus={true}>
          {recommendedColors?.slice(0, openedColorsGroup).map(({traitKey, colors}, index) => <div key={index}
                                                                                          className={"flex flex-col justify-center mb-12"}>
            {index > 0 && <div className={"mb-4 text-center"}>
              <span className={"text-neutral-500 font-semibold"}>These colors are more {traitName(traitKey)}</span>
            </div>}
            {colors.map((colorDefinition, index) => <div key={index} className="flex flex-col items-center gap-2 text-center">
              <Radio
                  className="hidden peer"
                  id={`${questionId}.${renderColorDefinitionKey(colorDefinition)}`}
                  value={renderColorDefinitionKey(colorDefinition)}
                  checked={renderSelectedColorsKey(value) === renderColorDefinitionKey(colorDefinition)}
                  onChange={() => {
                    onChange(questionId, getSelectedColors(colorDefinition, traitKey));
                  }}
              />
              <Label htmlFor={`${questionId}.${renderColorDefinitionKey(colorDefinition)}`}
                     className={"w-full h-24 p-5 inline-flex my-1 text-black bg-white border-2 border-neutral-50 rounded-2xl cursor-pointer peer-checked:border-bc-blue hover:text-neutral-600 hover:border-neutral-200"}>
                  <div className={"grow-[4]"}
                       style={{backgroundColor: colorDefinition.primary}}
                  />
                  <div className={"grow-[2]"}
                       style={{backgroundColor: calculateBackgroundColor(colorDefinition.primary)}}
                  />
                  <div className={"grow-[2]"}
                       style={{backgroundColor: colorDefinition.secondary}}
                  />
                  <div className={"grow-[1]"}
                       style={{backgroundColor: colorDefinition.accent}}
                  />
              </Label>
            </div>)}
          </div>)}
          {openedColorsGroup < (recommendedColors?.length ?? 0) && <div className={"mx-auto"}>
              <Button
                  className={"px-12 rounded-lg"}
                  color={"gray"}
                  onClick={() => handleShowMore()}
                  ref={showMoreRef}
              >
                  <div className={"flex flex-col"}>
                      <div><span className={"text-neutral-500 font-bold"}>Show more</span></div>
                      <div><FontAwesomeIcon icon={faChevronDown} size={"2xl"}/></div>
                  </div>
              </Button>
          </div>}
        </fieldset>
      </div>
    </div>
  </div>
}
